import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Bar } from '../../models/bar.model';
import { BarList, ListType } from '../../models/bar-list.model';
import { BarListService } from '../../services/bar-list.service';
import { BarEntry } from '../../components/bar-entry/bar-entry';
import { environment } from '../../../environments/environment';

type SortOrder = 'alphabetically' | 'recent';

@Component({
  selector: 'app-list',
  imports: [CommonModule, FormsModule, BarEntry],
  templateUrl: './list.html',
  styleUrl: './list.scss',
})
export class ListDetail implements OnInit {
  list: BarList | null = null;
  isLoading = true;

  // Filter & sort
  searchTerm = '';
  order: SortOrder = 'alphabetically';
  allBars: Bar[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private barListService: BarListService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    setTimeout(() => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);

    const listId = this.route.snapshot.paramMap.get('id');
    if (listId) {
      await this.loadList(listId);
    } else {
      this.isLoading = false;
    }
  }

  async loadList(listId: string) {
    try {
      this.isLoading = true;
      this.cdr.detectChanges();
      this.list = await this.barListService.getBarList(listId);
      this.allBars = [...(this.list?.bars ?? [])];
    } catch (error) {
      console.error('Error loading list:', error);
      this.list = null;
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  get filteredBars(): Bar[] {
    let bars = [...this.allBars];

    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase().trim();
      bars = bars.filter(b => b.name?.toLowerCase().includes(term));
    }

    if (this.order === 'alphabetically') {
      bars.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase(), 'es'));
    } else {
      bars.sort((a, b) => (b.lastUpdate ?? 0) - (a.lastUpdate ?? 0));
    }

    return bars;
  }

  setOrder(order: SortOrder) {
    this.order = order;
  }

  clearSearch() {
    this.searchTerm = '';
  }

  // Share
  copied = false;

  get shareUrl(): string {
    if (!this.list?.id) return '';
    return `${environment.shareUrl.protocol}://${environment.shareUrl.domain}/${environment.shareUrl.path}?listId=${this.list.id}`;
  }

  async shareList() {
    const url = this.shareUrl;
    if (!url) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: this.list?.name ?? 'Lista en BarBook',
          text: `Mira esta lista en BarBook: ${this.list?.name}`,
          url
        });
        return;
      } catch (e) {
        // User cancelled or error — fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.copied = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.copied = false;
        this.cdr.detectChanges();
      }, 2000);
    } catch (e) {
      console.error('Error copying URL:', e);
    }
  }

  goBack() {
    this.router.navigate(['/search']);
  }

  onBarSelected(barId: string) {
    this.router.navigate(['/bar', barId]);
  }

  get listTypeLabel(): string {
    if (!this.list?.type && this.list?.type !== 0) return 'Pública';
    switch (this.list.type) {
      case ListType.private:  return 'Privada';
      case ListType.shared:   return 'Compartida';
      case ListType.public:
      default:                return 'Pública';
    }
  }

  get lastUpdateDate(): string {
    if (!this.list?.lastUpdate) return '';
    return new Date(this.list.lastUpdate).toLocaleDateString('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  }
}
